import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { BackButton, EditButton } from "../../../../../Components/Utils/Buttons";
import Breadcrumb from "../../../../../Components/Utils/Breadcrumb";
import NuDate from "../../../../../Components/Utils/NuDate";
import axiosInstance from "../../../../../Services/axiosCommon";
import { GetAccess, GetRole } from "../../../../../Components/Utils/roles";

export default function ViewMeetingMinutesPage() {
  const { meetingPointId } = useParams();
  const location = useLocation();
  const stateData = location.state?.data;
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const [data, setData] = useState(stateData || null);
  const [loading, setLoading] = useState(!stateData);

  useEffect(() => {
    if (!stateData && meetingPointId) {
      setLoading(true);
      axiosInstance
        .get(`/meetings/points/${meetingPointId}`)
        .then((res) => {
          if (res?.data?.data) {
            setData(res.data.data);
          } else if (res?.data) {
            setData(res.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching meeting details:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [stateData, meetingPointId]);

  const targetId = meetingPointId || data?.id || data?.meeting_id;
  const actionItems = Array.isArray(data?.content?.action_items)
    ? data.content.action_items
    : Array.isArray(data?.content)
      ? data.content
      : Array.isArray(data?.action_items)
        ? data.action_items
        : [];

  const participantsList = Array.isArray(data?.participants)
    ? data.participants
    : [];

  const organizerName =
    data?.organizer_name ||
    data?.organizer?.name ||
    data?.organizerName ||
    (data?.organizer_id ? `User #${data.organizer_id}` : "-");

  const timeRange =
    data?.start_time || data?.startTime
      ? `${data?.start_time || data?.startTime}${data?.end_time || data?.endTime
        ? ` - ${data?.end_time || data?.endTime}`
        : ""
      }`
      : "-";

  return (
    <div className="w-full h-full py-2 px-4">
      {/* Top Bar */}
      <div className="w-full flex flex-row justify-between items-center gap-2 mb-3">
        <Breadcrumb
          items={[
            {
              name: "Meeting",
              link: "/meeting/meeting-points",
            },
            {
              name: "Meeting Minutes",
              link: "/meeting/meeting-points",
            },
            {
              name: "View Meeting Minutes",
            },
          ]}
        />
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/meeting/meeting-points"
            state={{
              currentPage: pageIndex,
              searchQuery: searchQuery !== "" ? searchQuery : "",
            }}
          >
            <BackButton link={true} />
          </Link>
          {(GetAccess("meeting_points_edit") || GetRole()) && (
            <Link
              to={`/meeting/meeting-points/edit/${targetId}`}
              state={{
                data: data,
                from: `/meeting/meeting-points/view/${targetId}`,
                pageIndex: pageIndex,
                searchQuery: searchQuery,
              }}
            >
              <EditButton height="8" />
            </Link>
          )}
        </div>
      </div>

      {/* Main Details Card */}
      <div className="w-full mx-auto min-h-[calc(100vh-145px)] h-auto bg-white rounded-md overflow-y-auto mt-4 p-2 sm:p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
          <h2 className="text-base font-medium text-[#111537] p-2">
            Meeting Details
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <span className="text-sm font-medium text-gray-500">
              Loading meeting details...
            </span>
          </div>
        ) : (
          <div className="w-full flex flex-wrap">
            {/* Field Grid matching standard project styling */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 my-2 px-2">
              {/* <div className="w-full flex px-2">
                <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                  Meeting ID
                </p>
                <p className="w-1/2 py-2 text-xs text-[#606279]">
                  : {targetId || "-"}
                </p>
              </div> */}
              <div className="w-full flex px-2">
                <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                  Title
                </p>
                <p className="w-1/2 py-2 text-xs text-[#606279]">
                  : {data?.meeting_title || data?.title || "-"}
                </p>
              </div>
              <div className="w-full flex px-2">
                <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                  Date
                </p>
                <p className="w-1/2 py-2 text-xs text-[#606279]">
                  : {data?.date ? <NuDate value={data.date} format="DD-MMM-YYYY" /> : "-"}
                </p>
              </div>
              <div className="w-full flex px-2">
                <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                  Time
                </p>
                <p className="w-1/2 py-2 text-xs text-[#606279]">
                  : {timeRange}
                </p>
              </div>
              <div className="w-full flex px-2">
                <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                  Location / Room
                </p>
                <p className="w-1/2 py-2 text-xs text-[#606279]">
                  : {data?.location || "-"}
                </p>
              </div>
              <div className="w-full flex px-2">
                <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                  Category
                </p>
                <p className="w-1/2 py-2 text-xs text-[#606279]">
                  : {data?.category || "-"}
                </p>
              </div>
              <div className="w-full flex px-2">
                <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                  Status
                </p>
                <p className="w-1/2 py-2 text-xs text-[#606279]">
                  : {data?.status || "-"}
                </p>
              </div>
              <div className="w-full flex px-2">
                <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                  Organizer
                </p>
                <p className="w-1/2 py-2 text-xs text-[#606279]">
                  : {organizerName}
                </p>
              </div>
              {/* <div className="w-full flex px-2">
                <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                  Total Action Items
                </p>
                <p className="w-1/2 py-2 text-xs text-[#606279]">
                  : {actionItems.length}
                </p>
              </div> */}
            </div>

            {/* Invited Participants - Only Show Names */}
            <div className="w-full flex px-4 my-2">
              <div className="w-full flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                <p className="w-full sm:w-1/6 min-w-[140px] text-xs font-medium text-[#111537]">
                  Invited Participants 
                  {/* ({participantsList.length}) */}
                </p>
                <div className="w-full sm:w-5/6 text-xs text-[#606279] flex flex-wrap items-center gap-2">
                  <span className="hidden sm:inline mr-1">:</span>
                  {participantsList.length > 0 ? (
                    participantsList.map((p, idx) => {
                      const pName =
                        typeof p === "object"
                          ? p.name ||
                          p.user_name ||
                          p.userName ||
                          p.user?.name ||
                          `Participant #${p.id || p.participant_id}`
                          : `Participant #${p}`;
                      return (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-1 rounded bg-slate-100 text-[#111537] text-xs font-medium border border-slate-200/80"
                        >
                          {pName}
                        </span>
                      );
                    })
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Points / Items Table */}
            <div className="w-full px-2 mt-4">
              <div className="w-full bg-white overflow-x-auto rounded-lg border border-gray-200/80">
                <div className="min-w-[650px]">
                  <div className="w-full py-2.5 bg-baseColor-primary flex justify-between px-3 rounded-t-lg">
                    <p className="w-[5%] text-left text-white text-xs font-medium">
                      S.No
                    </p>
                    <p className="w-[30%] text-left text-white text-xs font-medium">
                      Description
                    </p>
                    <p className="w-[20%] text-left text-white text-xs font-medium">
                      Action By
                    </p>
                    <p className="w-[15%] text-left text-white text-xs font-medium">
                      Due Date
                    </p>
                    <p className="w-[15%] text-left text-white text-xs font-medium">
                      Status
                    </p>
                    <p className="w-[15%] text-left text-white text-xs font-medium">
                      Comments
                    </p>
                  </div>
                  {actionItems.length === 0 ? (
                    <div className="text-center py-6 text-xs text-gray-400">
                      No action items recorded for this meeting.
                    </div>
                  ) : (
                    actionItems.map((item, index) => {
                      const matchedParticipant = participantsList.find(
                        (p) =>
                          String(p.id || p.participant_id) ===
                          String(item.action_by)
                      );
                      const actionByName =
                        item.action_by_name ||
                        item.actionByName ||
                        item.assignee_name ||
                        (matchedParticipant
                          ? matchedParticipant.name ||
                          matchedParticipant.user_name ||
                          matchedParticipant.userName ||
                          matchedParticipant.user?.name ||
                          `Participant #${item.action_by}`
                          : item.action_by
                            ? `Participant #${item.action_by}`
                            : "-");

                      return (
                        <div
                          key={index}
                          className="w-full py-2 flex justify-between items-center odd:bg-white even:bg-slate-100 px-3 border-b border-gray-100 last:border-none text-xs"
                        >
                          <p className="w-[5%] text-left text-gray-600">
                            {index + 1}
                          </p>
                          <p className="w-[30%] text-left font-medium text-black">
                            {item.description || "-"}
                          </p>
                          <p className="w-[20%] text-left text-gray-700">
                            {actionByName}
                          </p>
                          <p className="w-[15%] text-left text-gray-600">
                            {item.due_date ? (
                              <NuDate
                                value={item.due_date}
                                format="DD-MMM-YYYY"
                              />
                            ) : (
                              "-"
                            )}
                          </p>
                          <p className="w-[15%] text-left text-gray-600">
                            {item.status || "Pending"}
                          </p>
                          <p className="w-[15%] text-left text-gray-500">
                            {item.comments || "-"}
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
